import { Typography } from '@material-ui/core';
import React, { FC, memo } from 'react';
import { useIntl } from 'react-intl';

import ApplicantTableV2Cell from 'containers/ApplicantTableV2/components/ApplicantTableV2Cell';
import { COLUMN_TYPES } from 'containers/ApplicantTableV2/constants';
import { Applicant } from 'containers/ApplicantTableV2/types';
import {
  RECOMMENDATION_LABELS,
  RecommendationKey,
} from 'containers/ScorecardModals/ScorecardResults/scorecardResults';

const EMPTY_STATE = '—';

const RECOMMENDATION_ORDER: RecommendationKey[] = [
  'strong_no',
  'no',
  'yes',
  'strong_yes',
];

export interface ApplicantTableV2ScorecardScoreCellProps {
  applicant: Applicant;
}

// The applicant's average score across all submitted scorecard evaluations,
// with one line per recommendation that has at least one submission. An
// applicant with no submitted scorecards renders the table's em-dash empty
// state.
export const ApplicantTableV2ScorecardScoreCell: FC<
  ApplicantTableV2ScorecardScoreCellProps
> = ({ applicant }) => {
  const { formatMessage, formatNumber } = useIntl();
  const score = applicant.score_card_score;
  const counts = applicant.score_card_recommendations;
  const recommendations = RECOMMENDATION_ORDER.filter(
    key => (counts?.[key] ?? 0) > 0,
  );

  if (score == null && recommendations.length === 0) {
    return (
      <ApplicantTableV2Cell columnType={COLUMN_TYPES.SCORECARD_SCORE}>
        <Typography variant="caption" align="left" display="block">
          {EMPTY_STATE}
        </Typography>
      </ApplicantTableV2Cell>
    );
  }

  return (
    <ApplicantTableV2Cell columnType={COLUMN_TYPES.SCORECARD_SCORE}>
      <>
        {score != null && (
          <Typography variant="caption" align="left" display="block">
            {formatNumber(score, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 2,
            })}
          </Typography>
        )}
        {recommendations.map(key => (
          <Typography
            key={key}
            variant="caption"
            align="left"
            display="block"
            color="textSecondary"
          >
            {`${formatMessage(RECOMMENDATION_LABELS[key])} - ${formatNumber(
              counts?.[key] ?? 0,
            )}`}
          </Typography>
        ))}
      </>
    </ApplicantTableV2Cell>
  );
};

export default memo(ApplicantTableV2ScorecardScoreCell);
